import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "./AuthContext";
import { Pattern, PatternRecord, PatternType, StoredPattern, patternToColors, rowToStoredPattern } from "../types/pattern.model";

// The fields the "Save Pattern" flow can write. Deliberately narrower than
// PatternAttributes: no `id` (assigned by Postgres on insert, doesn't exist
// yet when calling addPattern) and no `archivedAt` (archiving is its own
// action outside the save flow, so a pattern's archive state is never
// touched here).
export interface PatternSaveInput {
  name: string | null;
  collectionId: string | null;
}

interface PatternContextType {
  patterns: Record<PatternType, StoredPattern[]>;
  addPattern: (patternType: PatternType, pattern: Pattern, input: PatternSaveInput) => Promise<StoredPattern>;
  updatePattern: (patternType: PatternType, id: string, newPattern: Pattern, input: PatternSaveInput) => Promise<void>;
  deletePattern: (patternType: PatternType, id: string) => Promise<void>;
  archivePattern: (patternType: PatternType, id: string) => Promise<void>;
  unarchivePattern: (patternType: PatternType, id: string) => Promise<void>;
  // Local-only state sync: the DB's `on delete set null` FK already clears
  // collection_id server-side when a collection is deleted, but this
  // context's cached patterns need the same update to reflect it without
  // a refetch.
  clearCollection: (collectionId: string) => void;
}

const PatternContext = createContext<PatternContextType | undefined>(undefined);

export const PatternProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const { session } = useAuth();
  const [patterns, setPatterns] = useState<Record<PatternType, StoredPattern[]>>({
    lightning: [],
    'twisted-stripe': []
  });

  useEffect(() => {
    if (!session) return;

    supabase.from('patterns').select('*').then(({ data, error }) => {
      if (error || !data) return;

      const loadedPatterns: Record<PatternType, StoredPattern[]> = {
        lightning: [],
        'twisted-stripe': []
      };
      (data as PatternRecord[]).forEach(row => {
        loadedPatterns[row.pattern_type].push(rowToStoredPattern(row));
      });
      setPatterns(loadedPatterns);
    });
  }, [session]);

  const addPattern = async (patternType: PatternType, pattern: Pattern, input: PatternSaveInput): Promise<StoredPattern> => {
    if (!session) throw new Error("Not signed in.");

    const { data, error } = await supabase
      .from('patterns')
      .insert({
        user_id: session.user.id,
        pattern_type: patternType,
        colors: patternToColors(pattern, patternType),
        name: input.name,
        collection_id: input.collectionId,
      })
      .select()
      .single();

    if (error || !data) throw new Error(error?.message ?? "Failed to save pattern.");

    const storedPattern = rowToStoredPattern(data as PatternRecord);
    setPatterns(prev => ({
      ...prev,
      [patternType]: [...prev[patternType], storedPattern],
    }));
    return storedPattern;
  };

  const updatePattern = async (patternType: PatternType, id: string, newPattern: Pattern, input: PatternSaveInput): Promise<void> => {
    const { error } = await supabase
      .from('patterns')
      .update({ colors: patternToColors(newPattern, patternType), name: input.name, collection_id: input.collectionId })
      .eq('id', id);

    if (error) throw new Error(error.message);

    setPatterns(prev => ({
      ...prev,
      [patternType]: prev[patternType].map(p => p.id === id ? { ...p, ...newPattern, name: input.name, collectionId: input.collectionId } : p),
    }));
  };

  const deletePattern = async (patternType: PatternType, id: string): Promise<void> => {
    const { error } = await supabase.from('patterns').delete().eq('id', id);

    if (error) throw new Error(error.message);

    setPatterns(prev => ({
      ...prev,
      [patternType]: prev[patternType].filter(p => p.id !== id),
    }));
  };

  const setArchivedAt = async (patternType: PatternType, id: string, archivedAt: string | null): Promise<void> => {
    const { error } = await supabase.from('patterns').update({ archived_at: archivedAt }).eq('id', id);

    if (error) throw new Error(error.message);

    setPatterns(prev => ({
      ...prev,
      [patternType]: prev[patternType].map(p => p.id === id ? { ...p, archivedAt } : p),
    }));
  };

  const archivePattern = (patternType: PatternType, id: string) => setArchivedAt(patternType, id, new Date().toISOString());

  const unarchivePattern = (patternType: PatternType, id: string) => setArchivedAt(patternType, id, null);

  const clearCollection = (collectionId: string) => {
    setPatterns(prev => ({
      lightning: prev.lightning.map(p => p.collectionId === collectionId ? { ...p, collectionId: null } : p),
      'twisted-stripe': prev['twisted-stripe'].map(p => p.collectionId === collectionId ? { ...p, collectionId: null } : p),
    }));
  };

  return (
    <PatternContext.Provider value={{ patterns, addPattern, updatePattern, deletePattern, archivePattern, unarchivePattern, clearCollection }}>
      {children}
    </PatternContext.Provider>
  );
};

// Raw access to both pattern types at once, for cross-type consumers
// (collections span both pattern types, unlike usePatterns below).
export const usePatternContext = (): PatternContextType => {
  const context = useContext(PatternContext);

  if (context === undefined) {
    throw new Error('usePatternContext must be used within a PatternProvider');
  }

  return context;
};

// custom hook to use pattern context
export const usePatterns = (patternType: PatternType) => {
  const context = useContext(PatternContext);

  if (context === undefined) {
    throw new Error('usePatterns must be used within a PatternProvider');
  }

  return {
    patterns: context.patterns[patternType],
    addPattern: (pattern: Pattern, input: PatternSaveInput) => context.addPattern(patternType, pattern, input),
    updatePattern: (id: string, newPattern: Pattern, input: PatternSaveInput) => context.updatePattern(patternType, id, newPattern, input),
    deletePattern: (id: string) => context.deletePattern(patternType, id),
    archivePattern: (id: string) => context.archivePattern(patternType, id),
    unarchivePattern: (id: string) => context.unarchivePattern(patternType, id),
  };
};
